'use client';

import { RootRichText } from '@cinemark/content-api-client';
import type { DefaultNodeTypes, SerializedBlockNode } from '@payloadcms/richtext-lexical';
import type { SerializedEditorState, SerializedLexicalNode } from '@payloadcms/richtext-lexical/lexical';
import { JSXConverters, type JSXConvertersFunction, RichText, SerializedLexicalNodeWithParent } from '@payloadcms/richtext-lexical/react';
import { RichTextAccordionBlockComponent } from './blocks/accordion';
import { RichTextExternalVideoBlockComponent } from './blocks/external-video';
import { RichTextGridBlockComponent } from './blocks/grid';
import { RichTextHorizontalRuleBlockComponent } from './blocks/horizontal-rule';
import { RichTextIframeEmbedBlockComponent } from './blocks/iframe-embed';
import { RichTextSocialShareBlockComponent } from './blocks/social-share/social-share';
import { RichTextLandmarkConverterComponent } from './landmark-converter';
import { RichTextLinkConverterComponent } from './link-converter';
import { RichTextRelationshipConverterComponent } from './relationship-converter';
import { RichTextTableCellConverterComponent, RichTextTableConverterComponent, RichTextTableRowConverterComponent } from './table-converter';
import { RichTextTextConverterComponent } from './text-converter';
import { RichTextUploadConverterComponent } from './upload-converter';

export type NodesToJSX = (args: {
  converters?: JSXConverters;
  disableIndent?: boolean | string[];
  disableTextAlign?: boolean | string[];
  nodes: SerializedLexicalNode[];
  parent?: SerializedLexicalNodeWithParent;
}) => React.ReactNode[];

type RichTextRendererProps = {
  className?: string;
  content: RootRichText | SerializedEditorState | undefined;
  renderTextOnly?: boolean;
};

export const RichTextRenderer = ({ className, content, renderTextOnly }: RichTextRendererProps): React.JSX.Element | null => {
  if (!content) return null;

  const jsxConverters: JSXConvertersFunction<DefaultNodeTypes> = ({ defaultConverters }) => ({
    ...defaultConverters,
    blocks: {
      accordion: ({ node }: { node: SerializedBlockNode; }): React.JSX.Element | null => {
        return (
          <RichTextAccordionBlockComponent node={!renderTextOnly ? node : undefined} />
        );
      },
      externalVideo: ({ node }: { node: SerializedBlockNode; }): React.JSX.Element | null => {
        return (
          <RichTextExternalVideoBlockComponent node={!renderTextOnly ? node : undefined} />
        );
      },
      grid: ({ node }: { node: SerializedBlockNode; }): React.JSX.Element | null => {
        return (
          <RichTextGridBlockComponent node={!renderTextOnly ? node : undefined} />
        );
      },
      horizontalRule: ({ node }: { node: SerializedBlockNode; }): React.JSX.Element | null => {
        return (
          <RichTextHorizontalRuleBlockComponent node={!renderTextOnly ? node : undefined} />
        );
      },
      iframeEmbed: ({ node }: { node: SerializedBlockNode; }): React.JSX.Element | null => {
        return (
          <RichTextIframeEmbedBlockComponent node={!renderTextOnly ? node : undefined} />
        );
      },
      socialShare: ({ node }: { node: SerializedBlockNode; }): React.JSX.Element | null => {
        return (
          <RichTextSocialShareBlockComponent node={!renderTextOnly ? node : undefined} />
        );
      },
    },
    ['html-converter-link-node']: ({ node, nodesToJSX }): React.JSX.Element | null => {
      node.type = 'link';
      return <RichTextLinkConverterComponent node={node}
        nodesToJSX={nodesToJSX}
      />;
    },
    landmark: ({ node, nodesToJSX }): React.JSX.Element | null => {
      return <RichTextLandmarkConverterComponent node={node}
        nodesToJSX={nodesToJSX}
      />;
    },
    link: ({ node, nodesToJSX }): React.JSX.Element | null => {
      return <RichTextLinkConverterComponent node={node}
        nodesToJSX={nodesToJSX}
      />;
    },
    relationship: ({ node }): React.JSX.Element | null => {
      return <RichTextRelationshipConverterComponent node={!renderTextOnly ? node : undefined} />;
    },
    table: ({ node, nodesToJSX }): React.JSX.Element | null => {
      return <RichTextTableConverterComponent node={!renderTextOnly ? node : undefined}
        nodesToJSX={nodesToJSX}
      />;
    },
    tablecell: ({ node, nodesToJSX }): React.JSX.Element | null => {
      return <RichTextTableCellConverterComponent node={!renderTextOnly ? node : undefined}
        nodesToJSX={nodesToJSX}
      />;
    },
    tablerow: ({ node, nodesToJSX }): React.JSX.Element | null => {
      return <RichTextTableRowConverterComponent node={!renderTextOnly ? node : undefined}
        nodesToJSX={nodesToJSX}
      />;
    },
    text: ({ node }): React.JSX.Element | null => {
      return <RichTextTextConverterComponent defaultTextConverter={defaultConverters.text}
        node={node}
      />;
    },
    upload: ({ node }): React.JSX.Element | null => {
      return <RichTextUploadConverterComponent node={!renderTextOnly ? node : undefined} />;
    },
  });

  return <RichText className={className}
    converters={jsxConverters}
    data={content as SerializedEditorState}
  />;
};
